import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { int } from '../lib/format';
import { C, ink } from '../lib/theme';
import type { ClientSearchRow } from '../lib/types';
import { Skeleton } from './primitives';

/** Busca de clientes (PRD §4, seção 08 da Visão Geral). Debounce pra não disparar uma request por tecla. */
export function ClientSearch({ onOpenClient }: { onOpenClient: (masterId: string) => void }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<ClientSearchRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setRows([]); setError(null); setLoading(false); return; }

    const mine = ++seq.current;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.searchClients(term);
        if (mine !== seq.current) return;
        setRows(res); setError(null);
      } catch (err) {
        if (mine !== seq.current) return;
        setError(err instanceof ApiError ? err.message : 'Falha na busca.');
        setRows([]);
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    }, 280);

    return () => clearTimeout(t);
  }, [q]);

  return (
    <div>
      <input
        type="search" value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Buscar por master_id ou nome do cliente…"
        style={{
          width: '100%', boxSizing: 'border-box', font: 'inherit', fontSize: 13.5, padding: '11px 15px',
          borderRadius: 12, border: `1px solid ${ink(0.14)}`, background: 'var(--input-bg)',
          color: C.fg, outline: 'none',
        }}
      />

      {q.trim().length > 0 && q.trim().length < 2 && (
        <div style={{ fontSize: 12, color: ink(0.35), marginTop: 10 }}>digite ao menos 2 caracteres</div>
      )}
      {loading && <div style={{ marginTop: 10 }}><Skeleton height={44} radius={11} /></div>}
      {error && <div style={{ fontSize: 12.5, color: C.churn, marginTop: 10 }}>{error}</div>}
      {!loading && !error && q.trim().length >= 2 && rows.length === 0 && (
        <div style={{ fontSize: 12.5, color: ink(0.35), marginTop: 12 }}>Nenhum cliente encontrado para “{q.trim()}”.</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 12 }}>
        {rows.map((r) => {
          const phones = int(r.phone_count);
          const emails = int(r.email_count);
          const total = phones + emails;
          return (
            <button
              key={r.master_id} type="button" onClick={() => onOpenClient(r.master_id)} className="ko-opt"
              style={{
                display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
                border: `1px solid ${ink(0.08)}`, background: ink(0.04), cursor: 'pointer', font: 'inherit',
                padding: '11px 14px', borderRadius: 11,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: C.fg }}>{r.master_id}</div>
                {r.nome && <div style={{ fontSize: 11.5, color: ink(0.42), marginTop: 1 }}>{r.nome}</div>}
              </div>
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 99, flex: 'none',
                background: total > 0 ? 'rgba(46,204,113,0.13)' : 'rgba(242,85,90,0.13)',
                color: total > 0 ? C.green : C.churn,
                fontVariantNumeric: 'tabular-nums',
              }}>{phones} tel · {emails} email</span>
              <span style={{ color: ink(0.3), fontSize: 14, flex: 'none' }}>→</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
